"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
export default function InquiryPortal() {
  return <section data-section-id="24225" id="connect-with-our-team" className="bg-background text-foreground relative overflow-hidden py-24 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] opacity-40 mix-blend-multiply animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-[128px] opacity-40 mix-blend-multiply" />
      </div>

      <div className="relative w-full max-w-2xl mx-auto z-10">
        <div className="text-center mb-10 space-y-2">
          <h3 className="text-primary font-sans text-sm tracking-widest uppercase font-bold">
            Enterprise Voice Automation
          </h3>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground font-sans">
            Let’s Build the Future of Your Voice
          </h2>
          <p className="text-muted-foreground text-lg max-w-lg mx-auto font-sans">
            Speak with our AI specialists to handle call overflow, lead qualification, and 24/7 reception.
          </p>
        </div>

        <Card className="border-border bg-card/80 backdrop-blur-md shadow-2xl overflow-hidden rounded-[10px]">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl font-bold font-sans">Request a Demo</CardTitle>
            <CardDescription className="text-base">
              Tell us about your business needs. Our team will design a custom AI voice solution for you.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formId="mollycall_contact_form" sectionName="connect-with-our-team" defaultValues={{
            name: "",
            email: "",
            organization: "",
            phone: "",
            subject: "Request a Demo",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            organization: {
              required: true
            },
            phone: {
              required: false,
              pattern: "phone"
            },
            subject: {
              required: true
            },
            message: {
              required: true,
              minLength: 20,
              maxLength: 2000
            }
          }} submitText="Request Your Demo" successMessage="Thank you! A MollyCall.ai specialist will contact you shortly to discuss your custom voice solution.">
              <InquiryFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-sans">Request Sent!</h3>
        <p className="text-muted-foreground max-w-md text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 text-primary hover:bg-primary/5 rounded-[10px] font-medium">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-medium text-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-muted/50 border-input focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-[10px]" />
              </FormControl>
              <FormDescription className="text-xs">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-medium text-foreground">Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@company.com" {...field} className="bg-muted/50 border-input focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-[10px]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-medium text-foreground">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp." {...field} className="bg-muted/50 border-input focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-[10px]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-medium text-foreground">Business Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555 000 0000" {...field} className="bg-muted/50 border-input focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-[10px]" />
              </FormControl>
              <FormDescription className="text-xs">Optional – for faster follow-up</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-medium text-foreground">Inquiry Type</FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className="flex h-10 w-full items-center justify-between rounded-[10px] border border-input bg-muted/50 px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 appearance-none text-foreground transition-all duration-300">
                  <option value="Request a Demo" className="bg-background text-foreground">Request a Demo</option>
                  <option value="Inbound Solutions" className="bg-background text-foreground">Inbound Solutions</option>
                  <option value="Outbound Automation" className="bg-background text-foreground">Outbound Automation</option>
                  <option value="Enterprise Inquiry" className="bg-background text-foreground">Enterprise Inquiry</option>
                  <option value="Partnership Opportunity" className="bg-background text-foreground">Partnership Opportunity</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-medium text-foreground">Your Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your current call volume and automation goals..." {...field} className="min-h-[150px] bg-muted/50 border-input focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 resize-y rounded-[10px]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[10px] flex items-start gap-3 animate-in slide-in-from-top-2">
          <CircleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-destructive">Submission Error</h4>
            <p className="text-sm text-destructive/80 mt-1">{submitError}</p>
          </div>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-gradient-to-r from-primary to-blue-600 hover:opacity-90 transition-opacity shadow-lg shadow-primary/30 rounded-[10px] text-white">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Scheduling Your Demo...
          </> : <>
            Request Your Demo
            <Send className="ml-2 h-4 w-4" />
          </>}
      </Button>
    </Form>;
}